import { ref, onMounted, watch } from 'vue';
import { Duration } from '../animations/component-lib.esm.js';
const DEFAULT_OPTIONS = {
  duration: Duration.SHORTER,
  onComplete: () => {}
};

/**
 * A hook to simplify usage of the FLIP animation technique.
 * See [here](https://css-tricks.com/animating-layouts-with-the-flip-technique)
 * for more information about the FLIP technique.
 *
 * To make the animation work, in addition to calling this
 * hook you must do the following:
 * - Attach the ref returned from this hook to a parent
 *   element of all the elements you wish to animate
 * - Add a `data-flip` attribute with a value matching one
 *   of the `value`s that will be passed to this hook on the
 *   element that you wish to animate.
 * - Set `opacity: 0` in CSS on the element you wish to
 *   animate (the opacity will be overridden with inline
 *   styles).
 *
 *  @param currentValue The currently active value. When this
 * is updated an animation will occur from the element
 * representing the old value to the element representing
 * the new value.
 *
 * @returns A ref to be attached to a parent element of all
 * the elements you wish to animate.
 */
function useFlip(currentValue) {
  let animationOptions = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const options = {
    ...DEFAULT_OPTIONS,
    ...animationOptions
  };
  const root = ref(null);
  const prevValue = ref(null);
  function findElForValue(value) {
    return root.value.querySelector(`[data-flip="${value}"]`);
  }
  onMounted(() => {
    const currentEl = findElForValue(currentValue.value);
    if (currentEl) currentEl.style.opacity = '1';
    prevValue.value = currentValue.value;
  });
  watch(currentValue, value => {
    if (value === prevValue.value) return;
    const prevEl = findElForValue(prevValue.value);
    const nextEl = findElForValue(value);
    prevValue.value = value;

    // Hide the previous element and show the next one
    prevEl.style.opacity = '0';
    nextEl.style.opacity = '1';

    // F - First
    let prevRect = prevEl.getBoundingClientRect();
    // L - Last
    let nextRect = nextEl.getBoundingClientRect();

    // I - Invert
    let deltaX = prevRect.left - nextRect.left;
    let deltaW = prevRect.width / nextRect.width;
    let keyframes = [{
      transform: `translateX(${deltaX}px) scaleX(${deltaW})`
    }, {
      transform: 'translateX(0) scaleX(1)'
    }];
    let timing = {
      duration: options.duration,
      easing: 'cubic-bezier(0.5, 1, 0.89, 1)'
    };
    if (!nextEl.animate) return;
    nextEl.animate(keyframes, timing);

    // Will run when the animation has completed
    setTimeout(() => {
      nextEl.style.transform = null;
      options.onComplete();
    }, options.duration);
  });
  return root;
}
export { useFlip };